// Single home for resolving Document Center status labels + colors.
//
// The Document Center aggregates four document types, each with its own status
// enum AND its own existing i18n vocabulary. Rather than duplicate those labels,
// we delegate to each type's namespace so the Polish/English wording (incl.
// gender, e.g. invoice ISSUED -> "Wystawiona" vs WZ ISSUED -> "Wystawiony")
// stays owned by the source module.
//
//   PZ               -> pz:status.{lower}
//   WZ               -> wz:status.{lower}
//   SALES_INVOICE    -> invoices:status.{lower}
//   PURCHASE_INVOICE -> orders:supplierOrders.status.{UPPER}
//
// Unknown/missing keys fall back to the raw enum value so new backend statuses
// never render blank.
import type { TFunction } from 'i18next';
import type { DocumentCenterType } from './api/documentCenterApi';

// i18n key builder per document type. Returns the namespaced key whose label
// lives in the source module, or null when the type is unknown.
const statusKeyByType = (type: DocumentCenterType, status: string): string | null => {
  switch (type) {
    case 'PZ':
      return `pz:status.${status.toLowerCase()}`;
    case 'WZ':
      return `wz:status.${status.toLowerCase()}`;
    case 'SALES_INVOICE':
      return `invoices:status.${status.toLowerCase()}`;
    case 'PURCHASE_INVOICE':
      return `orders:supplierOrders.status.${status.toUpperCase()}`;
    default:
      return null;
  }
};

// Resolve the human-facing status label for a document row. Falls back to the
// raw value when no translation exists.
export const getDocumentCenterStatusLabel = (
  t: TFunction,
  type: DocumentCenterType,
  status?: string | null
): string => {
  if (!status) return '';
  const key = statusKeyByType(type, status);
  if (!key) return status;
  // i18next returns the key itself when missing -> treat that as "no translation".
  const label = t(key, { defaultValue: '' });
  return label || status;
};

// Semantic Ant Design Tag colors keyed by normalized (uppercase) status. Shared
// across all four document types so the column reads consistently regardless of
// source. Invariant: when any source enum (PZ/WZ/invoice/supplier-order) gains a
// status, add it here too — otherwise the tag falls back silently to 'default'
// (grey). The fallback is safe (never blank), so a missing entry is cosmetic.
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  SENT: 'processing',
  CONFIRMED: 'blue',
  ISSUED: 'processing',
  IN_PRODUCTION: 'gold',
  PARTIALLY_READY: 'cyan',
  READY: 'cyan',
  RELEASED: 'cyan',
  SHIPPED: 'purple',
  PARTIALLY_RECEIVED: 'gold',
  RECEIVED: 'green',
  ON_HOLD: 'magenta',
  PAID: 'green',
  CANCELLED: 'red',
};

export const getDocumentCenterStatusColor = (status?: string | null): string => {
  if (!status) return 'default';
  return STATUS_COLORS[status.toUpperCase()] || 'default';
};
